import { useCallback, useEffect, useRef, useState } from 'react';
import { useAndroidAlternativeBilling } from './useAndroidAlternativeBilling.hook';
import { Product } from '@/@types/upsell';
import { useUpsellPurchase } from '@/apiHooks/useUpsellPurchase.hook';
import { startAlternativeBilling } from '@/services/nativeBridge/NativeBridgeService';

export const useWohnenPlusLightUpsellPurchase = (onSuccess: () => void) => {
  const controllerRef = useRef<AbortController | null>(null);
  const [isAlternativeBillingLoading, setIsAlternativeBillingLoading] = useState(false);
  const [isAlternativeBillingError, setIsAlternativeBillingError] = useState(false);
  const { isRequired } = useAndroidAlternativeBilling();
  const {
    upsellPurchase,
    isLoading: isPurchaseLoading,
    isSuccess,
    isError: isPurchaseError,
    resetError: resetPurchaseError,
  } = useUpsellPurchase(onSuccess);

  const handleBuyNow = useCallback(async () => {
    if (controllerRef.current) return;
    if (!isRequired) {
      upsellPurchase(Product.WOHNEN_LIGHT);
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    setIsAlternativeBillingLoading(true);
    setIsAlternativeBillingError(false);

    const result = await startAlternativeBilling(controller.signal).catch(
      () => ({ status: 'failure', reason: 'INTERNAL_ERROR' }) as const,
    );
    if (controller.signal.aborted) return;

    controllerRef.current = null;
    setIsAlternativeBillingLoading(false);

    switch (result.status) {
      case 'success':
        upsellPurchase(Product.WOHNEN_LIGHT, result.externalTransactionToken);
        break;
      case 'unavailable':
      case 'failure':
        setIsAlternativeBillingError(true);
        break;
      case 'canceled':
      case 'aborted':
        break;
    }
  }, [isRequired, upsellPurchase]);

  const resetError = useCallback(() => {
    setIsAlternativeBillingError(false);
    resetPurchaseError();
  }, [resetPurchaseError]);

  useEffect(() => () => controllerRef.current?.abort(), []);

  return {
    handleBuyNow,
    isLoading: isAlternativeBillingLoading || isPurchaseLoading,
    isSuccess,
    isError: isAlternativeBillingError || isPurchaseError,
    resetError,
  };
};
